/**
 * ThemeContext.jsx — Light / Dark Theme Context
 *
 * Provides:
 *   - theme: 'dark' | 'light'  — current theme name
 *   - toggleTheme()            — switch between dark and light
 *
 * Persistence:
 *   Theme is saved to localStorage so it survives page refreshes.
 *
 * How it works:
 *   Sets a data-theme attribute on <html> which CSS uses to swap
 *   colour variables (see index.css [data-theme="light"] block).
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

// ─── Context ──────────────────────────────────────────────────────────────────

const ThemeContext = createContext(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }) {
  // Read saved preference; default to 'dark'
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('cai-theme') || 'dark';
    } catch {
      return 'dark';
    }
  });

  // Apply theme to <html data-theme="..."> whenever it changes.
  // CSS uses this attribute to override colour variables.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('cai-theme', theme);
    } catch {
      // localStorage not available (private browsing, etc.) — ignore
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useTheme — access the current theme and toggle function.
 *
 * Usage:
 *   const { theme, toggleTheme } = useTheme();
 */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
